import {
  MSG_TYPE_SYNC,
  MSG_TYPE_READY,
  MSG_TYPE_EMBEDDED_PROPS_CHANGED,
} from './messaging/constants';

export interface AppModel {
  info: object;
  name: string;
  url: string;
}

export interface NavDisplayConfig {
  shouldShowToolbar: boolean;
  shouldShowNav: boolean;
  overlayType?: string;
}

export interface BenderContext {
  staticDomain: string;
  staticDomainPrefix: string;
  currentProject: string;
  currentProjectVersion: string;
  depVersions: Record<string, string>;
  depPathPrefixes: Record<string, string>;
}

export type WindowWithBender = Window & {
  hubspot?: {
    bender?: BenderContext;
  };
};

/* -- Message Types -- */
export type AnyPayload = object | undefined;
export type PayloadWithType<P, T extends string = string> = P & { type: T };

export interface Message {
  id: string;
  format: string;
  from: string;
  to: string;
  v: number;
  payload: PayloadWithType<AnyPayload>;
}

export type MessageWithType<
  T extends string,
  P extends AnyPayload = AnyPayload,
> = Message & {
  payload: { type: T } & P;
};

export interface BroadcastMessage extends Message {
  group: string;
}

interface SyncMessagePayload {
  embedId: string;
  group: string;
  name: string;
  appData: any;
  embeddedProps?: object;
  data?: any;
}

export type SyncMessage = MessageWithType<
  typeof MSG_TYPE_SYNC,
  SyncMessagePayload
>;

export type ReadyMessage = MessageWithType<typeof MSG_TYPE_READY, AnyPayload>;

export type EmbeddedPropsChangedMessage = MessageWithType<
  typeof MSG_TYPE_EMBEDDED_PROPS_CHANGED,
  AnyPayload
>;

export type ControlMessage =
  | SyncMessage
  | ReadyMessage
  | EmbeddedPropsChangedMessage;

export type AnyMessage = Message | BroadcastMessage | ControlMessage;

export interface MessageCallback {
  (message: AnyMessage['payload']): void;
}

export const isBroadcastMessage = (
  message: AnyMessage
): message is BroadcastMessage => 'group' in message;

export type GetPayload<M extends AnyMessage> = Omit<M['payload'], 'type'>;

/* -- Host Context -- */
export interface HostSendMessageContext {
  sendMessage: (
    type: string,
    data: AnyPayload,
    callback?: MessageCallback
  ) => void;
  sendReplyMessage: (
    message: AnyMessage,
    type: string,
    data: AnyPayload,
    callback?: MessageCallback
  ) => void;
}

export interface HostContext extends HostSendMessageContext {
  id: string;
}

/* -- Embedded Context -- */
export interface EmbeddedSendMessageContext {
  sendMessage: (
    type: string,
    data?: AnyPayload,
    callback?: MessageCallback
  ) => void;
  sendReplyMessage: (
    replyToMessage: AnyMessage,
    type: string,
    data?: AnyPayload,
    callback?: MessageCallback
  ) => void;
  sendDirectedMessage: (
    type: string,
    data: AnyPayload,
    to: string,
    callback?: MessageCallback
  ) => void;
  sendBroadcastMessage: (
    type: string,
    data?: AnyPayload,
    group?: string
  ) => void;
  sendBroadcastReplyMessage: (
    replyToMessage: BroadcastMessage,
    type: string,
    data?: AnyPayload
  ) => void;
}

export interface EmbeddedContext extends EmbeddedSendMessageContext {
  id: string;
  name: string;
  app: AppModel;
  group?: string;
}
